import React, { useState } from 'react'
import { Copy, Check, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

const TOKEN_PLACEHOLDER = '<your-token>'

const CodeBlock = ({ code, className }) => {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      toast.success('Copied')
      setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <div className={cn('relative group', className)}>
      <pre className="text-xs bg-muted rounded-md p-3 pr-10 overflow-x-auto whitespace-pre-wrap break-all font-mono">{code}</pre>
      <button
        type="button"
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1 rounded hover:bg-background text-muted-foreground hover:text-foreground"
        title="Copy"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  )
}

const Steps = ({ children }) => <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">{children}</ol>

// Setup with a personal access token: scripts, CI, and tools that can't sign in. With `token`
// the snippets contain the real token (shown once right after creation).
const TokenSetup = ({ url, apiUrl, token }) => {
  const t = token || TOKEN_PLACEHOLDER
  const claudeCode = `claude mcp add --transport http buzzinga ${url} --scope user \\\n  --header "Authorization: Bearer ${t}"`
  const cursor = JSON.stringify({ mcpServers: { buzzinga: { url, headers: { Authorization: `Bearer ${t}` } } } }, null, 2)
  const desktop = JSON.stringify({
    mcpServers: {
      buzzinga: {
        command: 'npx',
        args: ['-y', 'mcp-remote', url, '--header', 'Authorization:${AUTH_HEADER}'],
        env: { AUTH_HEADER: `Bearer ${t}` }
      }
    }
  }, null, 2)
  const curl = `curl -H "Authorization: Bearer ${t}" \\\n  ${apiUrl || 'https://<your-server>/api'}/tasks/by-key/PC-42`

  return (
    <div className="space-y-3 text-sm">
      {!token && (
        <p className="text-muted-foreground">
          For scripts, CI, or a tool that can't sign in through the browser. Create a token above; these snippets are filled in for you right after.
        </p>
      )}
      <div className="space-y-1.5">
        <p className="font-medium">Claude Code</p>
        <CodeBlock code={claudeCode} />
      </div>
      <div className="space-y-1.5">
        <p className="font-medium">Cursor <span className="font-normal text-muted-foreground">(<code>~/.cursor/mcp.json</code>)</span></p>
        <CodeBlock code={cursor} />
      </div>
      <div className="space-y-1.5">
        <p className="font-medium">Claude Desktop through <code>mcp-remote</code> <span className="font-normal text-muted-foreground">(needs Node.js; <code>claude_desktop_config.json</code>)</span></p>
        <CodeBlock code={desktop} />
      </div>
      <div className="space-y-1.5">
        <p className="font-medium">REST API</p>
        <p className="text-muted-foreground">The same token works on the Project Central REST API (tasks, comments, time tracking; not employee or admin data):</p>
        <CodeBlock code={curl} />
      </div>
    </div>
  )
}

// How to connect each AI tool. Recommended: sign in with the browser (OAuth). Nothing to copy
// but the server address; the tool opens Buzzinga, you approve, and it is connected.
const ConnectAiToolsPanel = ({ mcpUrl, apiUrl, token, connectionDays }) => {
  const url = mcpUrl || 'https://<your-server>/api/mcp'
  if (token) return <TokenSetup url={url} apiUrl={apiUrl} token={token} />

  const isLocal = /^http:\/\/(localhost|127\.0\.0\.1)/.test(url)
  const claudeCode = `claude mcp add --transport http buzzinga ${url} --scope user`
  const mcpJson = JSON.stringify({ mcpServers: { buzzinga: { type: 'http', url } } }, null, 2)
  const cursor = JSON.stringify({ mcpServers: { buzzinga: { url } } }, null, 2)
  const days = connectionDays || 90

  return (
    <Tabs defaultValue="claude-code" className="w-full">
      <TabsList className="flex flex-wrap h-auto">
        <TabsTrigger value="claude-code">Claude Code</TabsTrigger>
        <TabsTrigger value="project">Team (.mcp.json)</TabsTrigger>
        <TabsTrigger value="claude-app">Claude app</TabsTrigger>
        <TabsTrigger value="cursor">Cursor</TabsTrigger>
        <TabsTrigger value="token">With a token</TabsTrigger>
      </TabsList>

      <TabsContent value="claude-code" className="space-y-2 text-sm">
        <Steps>
          <li>Run once in a terminal (<code>--scope user</code> makes it available in every project):</li>
        </Steps>
        <CodeBlock code={claudeCode} />
        <Steps>
          <li value={2}>In Claude Code type <code>/mcp</code>, pick <strong>buzzinga</strong> and choose <strong>Authenticate</strong>.</li>
          <li>Your browser opens Buzzinga. Check the permissions and click <strong>Approve</strong>. That's it.</li>
        </Steps>
        <p className="text-muted-foreground">
          No token to copy. Claude Code refreshes access by itself; after {days} days it asks you to sign in again.
          Then ask for example: <span className="italic">"Read PC-42 with its attachments and implement it, then log the time and move it to In Review."</span>
        </p>
      </TabsContent>

      <TabsContent value="project" className="space-y-2 text-sm">
        <p className="text-muted-foreground">
          Commit this as <code>.mcp.json</code> at the root of a repository. It holds no secret: each developer signs in with their own
          account the first time (<code>/mcp</code> → <strong>Authenticate</strong>).
        </p>
        <CodeBlock code={mcpJson} />
        <p className="text-muted-foreground">
          Claude Code asks once whether to trust the project's servers. If the VS Code extension keeps it "pending", add
          <code> "enabledMcpjsonServers": ["buzzinga"]</code> to <code>~/.claude/settings.json</code>.
        </p>
      </TabsContent>

      <TabsContent value="claude-app" className="space-y-2 text-sm">
        {isLocal && (
          <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>claude.ai and Claude Desktop connect from the internet, so this works only with the live (https) address, not with localhost.</span>
          </div>
        )}
        <Steps>
          <li>In claude.ai or Claude Desktop open <strong>Settings → Connectors</strong> and choose <strong>Add custom connector</strong>.</li>
          <li>Name it <strong>Buzzinga</strong> and paste this URL:</li>
        </Steps>
        <CodeBlock code={url} />
        <Steps>
          <li value={3}>Click <strong>Connect</strong>, then <strong>Approve</strong> in the Buzzinga page that opens.</li>
        </Steps>
      </TabsContent>

      <TabsContent value="cursor" className="space-y-2 text-sm">
        <p className="text-muted-foreground">Add to <code>~/.cursor/mcp.json</code>, then click <strong>Login</strong> next to buzzinga in Cursor's MCP settings and approve:</p>
        <CodeBlock code={cursor} />
        <p className="text-muted-foreground">If your Cursor version can't sign in this way, use a token instead (last tab).</p>
      </TabsContent>

      <TabsContent value="token">
        <TokenSetup url={url} apiUrl={apiUrl} />
      </TabsContent>
    </Tabs>
  )
}

export default ConnectAiToolsPanel
